import { useEffect, useRef, useState, useCallback } from 'react';

const WS_BASE = `wss://${window.location.host}/api/synapse`;
const API_KEY = 'synapse-dev'; // Same key as lib/api.ts

interface WebSocketState {
  connected: boolean;
  lastMessage: any | null;
  error: string | null;
}

export function useWebSocket(endpoint: string = '/ws/tasks') {
  const [state, setState] = useState<WebSocketState>({
    connected: false,
    lastMessage: null,
    error: null,
  });
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  const connect = useCallback(() => {
    // Prevent duplicate connections - check both OPEN and CONNECTING states
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) return;

    try {
      // Include API key as query parameter for WebSocket auth (browsers can't send headers)
      const ws = new WebSocket(`${WS_BASE}${endpoint}?token=${API_KEY}`);

      ws.onopen = () => {
        console.log('[WebSocket] Connected to', endpoint);
        setState(prev => ({ ...prev, connected: true, error: null }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setState(prev => ({ ...prev, lastMessage: data }));
        } catch (e) {
          console.warn('[WebSocket] Failed to parse message:', event.data);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected, code:', event.code);
        setState(prev => ({ ...prev, connected: false }));
        wsRef.current = null;

        // Auto-reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        setState(prev => ({ ...prev, error: 'Connection error' }));
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('[WebSocket] Failed to connect:', e);
      setState(prev => ({ ...prev, error: 'Failed to connect' }));
    }
  }, [endpoint]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const send = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    ...state,
    send,
    reconnect: connect,
  };
}

export default useWebSocket;
